import { Card, HeroFigure, Stat, StatusChip } from '../../components/data';
import { ErrorBanner, Loading } from '../../components/ui';
import { formatCompactCurrency, num, pct, secs } from '../../lib/format';
import { useResource } from '../../lib/hooks';
import { SEVERITY_BANDS, type AdminOverviewData, type AdminTab } from './adminTypes';
import { DeltaStat, RegisterEmpty } from './adminUi';

/**
 * What needs action today, across every register, worst first.
 *
 * The alert list is the point of this tab. Nine registers each with their own
 * review cadence is exactly the shape of thing where something quietly lapses —
 * an insurance policy, a notice deadline, a supplier QBR — and nobody opens nine
 * tabs every Monday to check. Everything here is a count of rows that breach a
 * rule, so every alert can be chased to a list.
 *
 * The panels below it are a glance per register, and each one links to the tab
 * that can do something about it.
 */
export function AdminOverview({ onNavigate }: { onNavigate: (tab: AdminTab) => void }) {
  const overview = useResource<AdminOverviewData>('/admin/analytics/overview');

  if (overview.error) return <ErrorBanner message={overview.error} />;
  if (!overview.data) return <Loading label="Loading the overview" />;

  const d = overview.data;
  const r = d.registers;
  const anythingRecorded =
    r.contracts + r.purchaseOrders + r.assets + r.sops + r.registrations + r.vendors + r.callRows + r.kpis >
    0;

  /* Nothing entered anywhere is a completely different page from nothing wrong.
     Said once, here, rather than repeated as nine empty panels. */
  if (!anythingRecorded) {
    return (
      <RegisterEmpty title="The registers are empty">
        <p>
          This module came across from the standalone admin dashboard with its screens intact and its
          data left behind. Everything that app displayed was generated by its seed script — thirty
          invented staff, six months of invented calls, twenty-eight invented contracts — and none of
          it belonged in the toolbox the leadership team runs on.
        </p>
        <p className="mt-2">
          So there is nothing here yet, and that is the honest starting point. Each tab is a register
          you fill in, and the analytics on it compute from what you enter. The two worth starting
          with are <strong>Contracts</strong>, because a notice deadline is the one thing here that
          costs money when it slips, and <strong>Registrations</strong>, because a lapsed insurance
          policy is the one that stops the business.
        </p>
      </RegisterEmpty>
    );
  }

  return (
    <div className="admin-stack">
      <Card
        title="Needs action"
        subtitle={
          d.alerts.length === 0
            ? 'Nothing across the registers is overdue or inside its notice window.'
            : `${num(d.alerts.length)} ${d.alerts.length === 1 ? 'thing' : 'things'} to deal with, most severe first`
        }
      >
        {d.alerts.length === 0 ? (
          <p className="sub">
            Every register that holds anything is inside its dates. Registers with nothing in them
            raise nothing — see the counts on the panels below.
          </p>
        ) : (
          <ul className="admin-alerts">
            {d.alerts.map((alert, i) => (
              <li key={`${alert.module}-${i}`}>
                <button
                  type="button"
                  className="admin-alert-row"
                  onClick={() => onNavigate(alert.tab as AdminTab)}
                >
                  <StatusChip band={SEVERITY_BANDS[alert.severity]}>{alert.module}</StatusChip>
                  <span className="message">{alert.message}</span>
                  <span className="go" aria-hidden="true">
                    →
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <div className="admin-panel-grid">
        <PanelCard
          title="Contracts"
          count={r.contracts}
          empty="No contracts recorded. This is the register where a missed notice date costs a year."
          onOpen={() => onNavigate('contracts')}
        >
          <HeroFigure
            label="Client MRR"
            value={formatCompactCurrency(d.contracts.clientMrr)}
            note={`${num(d.contracts.active)} active`}
          />
          <div className="grid grid-3">
            <Stat
              label="Notice ≤30d"
              value={d.contracts.noticeDeadline30}
              flagged={d.contracts.noticeDeadline30 > 0}
              foot="decision closing"
            />
            <Stat label="Expiring ≤90d" value={d.contracts.expiring90} foot="renewals ahead" />
            <Stat
              label="At risk"
              value={formatCompactCurrency(d.contracts.valueAtRisk90)}
              foot="annual value, 90d"
            />
          </div>
        </PanelCard>

        <PanelCard
          title="Procurement"
          count={r.purchaseOrders}
          empty="No purchase orders recorded. Quote turnaround and supplier on-time delivery compute from these."
          onOpen={() => onNavigate('procurement')}
        >
          <HeroFigure
            label="Open orders"
            value={num(d.procurement.openPoCount)}
            note={`${formatCompactCurrency(d.procurement.openPoValue)} committed`}
          />
          <div className="grid grid-2">
            <Stat
              label="Past promise"
              value={d.procurement.overdueReceipts}
              flagged={d.procurement.overdueReceipts > 0}
              foot="not yet received"
            />
            <Stat
              label="Spend, 60d"
              value={formatCompactCurrency(d.procurement.spend60d)}
              foot="at cost"
            />
          </div>
        </PanelCard>

        <PanelCard
          title="Depot"
          count={r.assets}
          empty="No depot stock recorded. Stock value, warranty position and audit currency compute from these."
          onOpen={() => onNavigate('depot')}
        >
          <HeroFigure
            label="Stock value"
            value={formatCompactCurrency(d.depot.stockValue)}
            note={`${num(d.depot.inStockUnits)} units on the shelf`}
          />
          <div className="grid grid-2">
            <Stat label="In RMA" value={d.depot.rmaOpen} flagged={d.depot.rmaOpen > 0} foot="awaiting a decision" />
            <Stat label="Warranty ≤90d" value={d.depot.warrantyExpiring90} foot="running out" />
          </div>
        </PanelCard>

        <PanelCard
          title="Registrations & insurance"
          count={r.registrations}
          empty="Nothing on the register. This is where the company registration, tax numbers, policies and licences are kept in date."
          onOpen={() => onNavigate('registrations')}
        >
          <HeroFigure
            label="Lapsed"
            value={num(d.registrations.lapsed)}
            note={`of ${num(d.registrations.total)} tracked`}
          />
          <div className="grid grid-2">
            <Stat
              label="Expiring ≤30d"
              value={d.registrations.expiring30}
              flagged={d.registrations.expiring30 > 0}
              foot="start these now"
            />
            <Stat label="Tracked" value={d.registrations.total} foot="registrations and policies" />
          </div>
        </PanelCard>

        <PanelCard
          title="SOPs"
          count={r.sops}
          empty="No procedures registered. This register answers which are out of date and who to chase — the documents themselves can live anywhere."
          onOpen={() => onNavigate('sops')}
        >
          <HeroFigure
            label="Overdue for review"
            value={num(d.sops.overdueReview)}
            note={`of ${num(d.sops.active)} active`}
          />
          <div className="grid grid-2">
            <Stat label="Due ≤30d" value={d.sops.dueIn30} foot="review coming up" />
            <Stat label="Active" value={d.sops.active} foot="live procedures" />
          </div>
        </PanelCard>

        <PanelCard
          title="Vendors"
          count={r.vendors}
          empty="No suppliers recorded. Spend concentration and QBR cadence compute from these."
          onOpen={() => onNavigate('vendors')}
        >
          <HeroFigure
            label="Annual spend"
            value={formatCompactCurrency(d.vendors.annualSpend)}
            note={`${num(d.vendors.total)} ${d.vendors.total === 1 ? 'supplier' : 'suppliers'}`}
          />
          <div className="grid grid-2">
            <Stat
              label="QBRs overdue"
              value={d.vendors.reviewsOverdue}
              flagged={d.vendors.reviewsOverdue > 0}
              foot="past the review date"
            />
            <Stat label="Strategic" value={d.vendors.strategic} foot="tier 1 relationships" />
          </div>
        </PanelCard>

        <PanelCard
          title="Scorecard"
          count={r.kpis}
          empty="No measures defined. The scorecard is where the admin function's own targets live."
          onOpen={() => onNavigate('scorecard')}
        >
          <HeroFigure
            label="Weighted health"
            value={d.scorecard.weightedHealth === null ? '—' : pct(d.scorecard.weightedHealth, 0)}
            note={d.scorecard.period}
          />
          <div className="grid grid-3">
            <Stat label="On target" value={d.scorecard.onTarget} foot="of the measured" />
            <Stat label="At risk" value={d.scorecard.atRisk} foot="past target, inside floor" />
            <Stat
              label="Off target"
              value={d.scorecard.offTarget}
              flagged={d.scorecard.offTarget > 0}
              foot="past the floor"
            />
          </div>
          {d.scorecard.noData > 0 && (
            <p className="sub mt-2">
              {num(d.scorecard.noData)} {d.scorecard.noData === 1 ? 'measure has' : 'measures have'} no
              result for {d.scorecard.period} and count toward neither.
            </p>
          )}
        </PanelCard>

        <PanelCard
          title="Inbound calls"
          count={r.callRows}
          /* The one register nobody types in by hand — see the exception noted
             at the end of migration 048. Its empty state says so rather than
             implying somebody forgot. */
          empty="No phone data loaded. Unlike the registers beside it, this one expects an export from the phone system rather than typing — a month at a time, one row per queue per day."
          onOpen={() => onNavigate('calls')}
        >
          <HeroFigure
            label="Service level, 30d"
            value={d.calls.serviceLevelPercent === null ? '—' : pct(d.calls.serviceLevelPercent)}
            note={`${num(d.calls.offered30d)} calls offered`}
          />
          <div className="grid grid-2">
            <DeltaStat
              label="Abandon rate"
              value={d.calls.abandonRatePercent}
              previous={d.calls.abandonRatePreviousPercent}
              format={(v) => pct(v)}
              betterWhen="lower"
            />
            <Stat
              label="Speed to answer"
              value={secs(d.calls.avgSpeedAnswerSec)}
              foot="volume-weighted"
            />
          </div>
        </PanelCard>
      </div>
    </div>
  );
}

/**
 * One register's glance.
 *
 * Renders its own empty state rather than zeroes when the register holds nothing.
 * A stock value of $0 and a depot nobody has entered look identical as a figure
 * and mean completely different things — one is a problem, the other is a blank
 * page waiting for a first row.
 */
function PanelCard({
  title,
  count,
  empty,
  onOpen,
  children,
}: {
  title: string;
  count: number;
  empty: string;
  onOpen: () => void;
  children: React.ReactNode;
}) {
  return (
    <Card
      title={title}
      actions={
        <button type="button" className="btn btn-ghost btn-sm" onClick={onOpen}>
          Open
        </button>
      }
    >
      {count === 0 ? <p className="sub">{empty}</p> : <div className="admin-panel-body">{children}</div>}
    </Card>
  );
}
